import { useState } from 'react'
import { useToast } from '@machobear/design-system'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { useMutation } from '@tanstack/react-query'
import {
  map,
  omit,
  pipe,
  filter,
  keys,
  pick,
  propEq,
  equals,
  reduce,
  prop,
  sortWith,
  descend
} from 'ramda'
import useProfile from '@/hooks/use-profile'
import { Wizard, WizardStep } from '@/components/modal-wizard-form'
import { useGetUsages } from '@/hooks/use-block-and-pay'
import queryClient from '@/components/query-client'
import useGetPromotions from '@/hooks/use-promotions'
import usePaymentMethod from '@/hooks/use-payments'
import { useWizardContext } from '@/components/modal-wizard-form/wizard-context'
import { useConsumeSyndication, usePurchaseSyndication } from '@/hooks/use-syndication'
import useGetPlanKey from '@/hooks/use-get-plan-key'
import { Product, PLinkData, PlanType } from '@/pages/UsageCredits/types'
import { usePaymentContext } from '@/components/ds-checkout-modal/payment-context'
import { AddingPaymentMethod } from '@/components/ds-checkout-modal/types'
import CheckoutStep from '@/components/checkout-step'
import {
  usePaymentValidation,
  initialValues as checkoutInitialValues
} from '@/hooks/use-payment-validation'
import { add } from '@/models/plans/apis'
import { DialogEventHandler } from '@/types'
import config from '@/config'
import SelectProductsStep from './select-products-step'
import AddonStep from './addon-step'
const isDev = config.BUILD_ENV === 'dev'

export const SYNDICATION_PLINK = isDev
  ? 'plink_1OXCEiGbTt9cjGfcryLdyaQq'
  : 'plink_1OoDwqKP0LaOcrHBzXjyieU8'
type Props = {
  closeModal: DialogEventHandler
  selectedListingId?: string
}
type FormValues = {
  platform: string[]
  facebookProduct?: string
  craigslistProduct?: string
  zumperProduct?: string
  addons: string[]
  analyticsProduct?: string
  default_payment_method?: boolean
  payment_method?: string
  first_name?: string
  last_name?: string
  street1?: string
  street2?: string
  city?: string
  state?: string
  country?: string
  zip?: string
  card_number?: string
  expiration_date?: string
  security_code?: string
  save_payment_method?: boolean
  save_billing_address?: boolean
}

type FormattedValues = {
  plan_name: 'syndication_single_package' | 'analytics-report-single-package'
  platform: 'craigslist' | 'facebook' | 'zumper' | null
  quantity: number
}[]

type FormattedItem = {
  priceId: string
  quantity: number
  useCredits: boolean
}

interface ApiResponse<T> {
  body: T
  response: {
    ok: boolean
    statusText: string
    body: {
      message?: string
    }
  }
}

const getFormattedValues = (
  values: Omit<FormValues, 'addons' | 'platform'>,
  products: Product[],
  addons: Product[]
) => {
  const formattedValues = pipe(
    Object.entries,
    map(([key, value]) => {
      let formattedValue

      if (key === 'analyticsProduct') {
        formattedValue = addons?.find(addon => addon?.priceId === value)
        return {
          plan_name: 'analytics-report-single-package',
          platform: null,
          quantity: formattedValue?.quantity
        }
      } else {
        formattedValue = products?.find(addon => addon?.priceId === value)
        return {
          plan_name: 'syndication_single_package',
          platform: formattedValue?.metadata?.platform,
          quantity: formattedValue?.quantity
        }
      }
    })
  )(values)

  return formattedValues as FormattedValues
}
const getFormattedItems = (
  values: Omit<FormValues, 'addons' | 'platform'>,
  products: Product[],
  addons: Product[],
  creditCount: number
) => {
  let count = creditCount
  const formattedValues = pipe(
    Object.entries,
    map(([key, value]): Omit<FormattedItem, 'useCredits'> & { addon: boolean } => {
      const isAddons = key === 'analyticsProduct'
      const dataSource = isAddons ? addons : products
      const product = dataSource?.find(addon => addon?.priceId === value)

      return {
        priceId: product?.priceId ?? '',
        quantity: product?.quantity ?? 0,
        addon: isAddons
      }
    }),
    sortWith([descend(prop('quantity'))]),
    map((item: Omit<FormattedItem, 'useCredits'> & { addon: boolean }) => {
      let useCredits = false
      if (item.addon) {
        return {
          priceId: item.priceId,
          quantity: item.quantity,
          useCredits: false
        }
      }
      const product = products?.find(addon => addon?.priceId === item.priceId)
      if (product && count - product?.quantity >= 0) {
        useCredits = true
        count -= product?.quantity
      }
      return {
        priceId: item.priceId,
        quantity: item.quantity,
        useCredits
      }
    })
  )(values)

  return formattedValues
}

const paymentFormKeys = pipe(keys)(checkoutInitialValues)

const getFilteredValues = pipe(omit(['addons', 'platform', ...paymentFormKeys]), filter(Boolean))

const getCount = pipe(
  reduce((acc: number, item: { quantity: number }) => {
    return acc + item?.quantity
  }, 0)
)

const ModalContent = ({ closeModal, selectedListingId = '' }: Props) => {
  const { t } = useTranslation()
  const [dryRunValues, setDryRunValues] = useState({})
  const profile = useProfile()
  const { data } = useGetUsages()
  const { setProduct, coupon } = usePaymentContext()
  const planText = useGetPlanKey()
  const useSyndicationCredits = useConsumeSyndication()
  const purchasedSyndication = usePurchaseSyndication()
  const { paymentInfo } = usePaymentMethod()
  const hasPaymentMethods = paymentInfo && paymentInfo?.length !== 0
  const { setIsSubmitting, setSubmittingFinished } = useWizardContext()

  const showToast = useToast()
  const [hasAddons, setHasAddons] = useState(false)
  const [hasEnoughCredits, setHasEnoughCredits] = useState(true)
  const state = hasPaymentMethods ? paymentInfo[0]?.state : 'BC'

  const { data: productData }: { data: PLinkData; isLoading: boolean; error: any } =
    useGetPromotions({
      country: 'CA',
      province: state,
      promotionId: SYNDICATION_PLINK,
      addon: true
    })

  const products = productData?.products?.[0]?.items
  const addons = Object.values(productData?.addons || {}) as Product[]
  const showCheckout = hasAddons || !hasEnoughCredits

  const currentPlan = profile?.company_info?.base_plan_name as PlanType
  const isBusinessPlan =
    currentPlan === 'business-plan-annual' ||
    currentPlan === 'business-plan-monthly' ||
    currentPlan === 'business-plan-special'

  const planSyndications =
    data?.blocks?.syndication?.value - data?.blocks?.syndication?.usage?.current_value
  const purchasedSyndications =
    data?.blocks?.syndication_additional_packages?.value -
    data?.blocks?.syndication_additional_packages?.usage?.current_value
  const creditCount = planSyndications + purchasedSyndications

  const showSubmitToasts = (count: number) => {
    if (count <= planSyndications) {
      showToast({
        key: `syndication-plan-${count}`,
        message: t('l.syndication.plan_credits.text', 'Plan credits updated!'),
        description: t(
          'l.syndication.plan_credits_used.text',
          '\u2022 Syndication: {{count}} credit(s) used',
          { count: count }
        ),
        tone: 'positive'
      })

      return
    }
    if (planSyndications === 0) {
      showToast({
        key: `syndication-purchased-${count}`,
        message: t('l.syndication.purchased_credits.text', 'Purchased credits updated!'),
        description: t(
          'l.syndication.plan_credits_used.text',
          '\u2022 Syndication: {{count}} credit(s) used',
          { count: count }
        ),
        tone: 'positive'
      })

      return
    }

    const purchasedCount = count - planSyndications
    showToast({
      key: `syndication-plan-${planSyndications}`,
      message: t('l.syndication.plan_credits.text', 'Plan credits updated!'),
      description: t(
        'l.syndication.plan_credits_used.text',
        '\u2022 Syndication: {{count}} credit(s) used',
        { count: planSyndications }
      ),
      tone: 'positive'
    })
    showToast({
      key: `syndication-purchased-${purchasedCount}`,
      message: t('l.syndication.purchased_credits.text', 'Purchased credits updated!'),
      description: t(
        'l.syndication.plan_credits_used.text',
        '\u2022 Syndication: {{count}} credit(s) used',
        { count: purchasedCount }
      ),
      tone: 'positive'
    })
  }

  const handleUseCredits = async (values: FormValues) => {
    const filteredValues = getFilteredValues(values)
    const formattedValues = getFormattedValues(filteredValues, products, addons)
    const formattedItems = getFormattedItems(filteredValues, products, addons, creditCount)
    const count = getCount(formattedItems)

    const { response, body } = (await useSyndicationCredits.mutateAsync({
      listingId: selectedListingId,
      items: formattedValues
    })) as ApiResponse<any>

    if (!response?.ok) {
      const errorMessage = body?.message ?? ''
      showToast({
        message: t(
          'l.syndication.purchase.error.text',
          'Sorry! {{ errorMessage }}. Please try again.',
          { errorMessage }
        ),
        tone: 'critical'
      })
      window?.dataLayer?.push({
        event: `usage-credit-${planText}-checkout-dialog-multiplatform-adv-submit-error`
      })
      setSubmittingFinished()
      return
    }
    showSubmitToasts(count)
    window?.dataLayer?.push({
      event: `usage-credit-${planText}-checkout-dialog-multiplatform-adv-submit-success`
    })

    setSubmittingFinished()
    queryClient.invalidateQueries({
      queryKey: ['blocks']
    })
    closeModal()
  }
  const purchaseAndSyndicate = async (paymentMethodId: string, values: FormValues) => {
    const filteredValues = getFilteredValues(values)

    const formattedItems = getFormattedItems(filteredValues, products, addons, creditCount)

    const items = pipe(
      filter(propEq('useCredits', false)),
      map(pick(['priceId', 'quantity']))
    )(formattedItems) as any

    const response = await purchasedSyndication.mutateAsync({
      paymentMethodId: paymentMethodId,
      couponCode: coupon,
      items: items,
      metadata: {
        pLink: SYNDICATION_PLINK
      }
    })

    if (!response?.response?.ok) {
      showToast({
        message: response?.body?.message,
        tone: 'critical'
      })

      setSubmittingFinished()
      return
    }

    await handleUseCredits(values)
  }

  const handleSubmitAddons = (values: any) => {
    const filteredValues = getFilteredValues(values)

    const formattedItems = getFormattedItems(filteredValues, products, addons, creditCount)

    if (showCheckout) {
      const state = hasPaymentMethods ? paymentInfo[0]?.state : (values.state ?? 'BC')
      const country = hasPaymentMethods
        ? paymentInfo[0]?.country
        : (values.country?.toUpperCase() ?? 'CA')
      if (!equals(dryRunValues, values)) {
        setDryRunValues(values)
        setProduct({ items: formattedItems, metadata: { pLink: SYNDICATION_PLINK } })
        queryClient.invalidateQueries({
          queryKey: ['dryRun', country, state, coupon]
        })
      }
    }
  }

  const addMethod = useMutation({
    mutationFn: (values: AddingPaymentMethod) => add(values)
  })
  const handlePurchaseWithoutPM = async (values: any) => {
    // setIsSubmitting()

    if (!hasPaymentMethods) {
      addMethod.mutate(
        {
          ...values,
          card_number: values.card_number,
          expiration_date: values.expiration_date.replace('/', '')
        },
        {
          onSuccess: async data => {
            await purchaseAndSyndicate(data[0].payment_method_id, values)
            window?.dataLayer?.push({
              event: `usage-credit-${planText}-checkout-payment-dialog-multiplatform-adv-submit-success`
            })
          },
          onError: () => {
            setSubmittingFinished()
            window?.dataLayer?.push({
              event: `usage-credit-${planText}-checkout-payment-dialog-multiplatform-adv-submit-error`
            })
          }
        }
      )
    }
  }

  // Purchase with existing payment method
  const handlePurchaseWithPM = async (values: FormValues) => {
    await purchaseAndSyndicate(paymentInfo[0].payment_method_id, values)
    window?.dataLayer?.push({
      event: `usage-credit-${planText}-addon-dialog-multiplatform-adv-submit-success`
    })
  }
  const onSubmit = (values: FormValues) => {
    setIsSubmitting()
    if (showCheckout) {
      if (hasPaymentMethods) {
        handlePurchaseWithPM(values)
        return
      } else {
        handlePurchaseWithoutPM(values)
        return
      }
    } else {
      handleUseCredits(values)
    }
  }

  const initialValues = {
    platform: [] as string[],
    facebookProduct: undefined,
    craigslistProduct: undefined,
    zumperProduct: undefined,
    addons: [] as string[],
    analyticsProduct: undefined,
    ...checkoutInitialValues
  }

  const paymentSchema = usePaymentValidation()
  const checkoutValidationSchema = hasPaymentMethods ? undefined : paymentSchema

  const continueButtonId = `usage-credit-${planText}-checkout-dialog-multiplatform-adv-continue`
  const submitButtonId = `usage-credit-${planText}-checkout-dialog-multiplatform-adv-submit`

  const addonSubmitButtonId = showCheckout
    ? `usage-credit-${planText}-addon-dialog-multiplatform-adv-submit-click`
    : submitButtonId
  const selectProductsSubmitButtonId = isBusinessPlan
    ? continueButtonId
    : `usage-credit-${planText}-checkout-dialog-multiplatform-adv-submit-click`
  return (
    <Wizard initialValues={initialValues} onSubmit={onSubmit}>
      <WizardStep
        validationSchema={Yup.object().shape({
          platform: Yup.array().min(
            1,
            t('l.usage.credits.syndication.error.text', 'Please select at least 1 plaform')
          )
        })}
        enabled
        submitButtonId={selectProductsSubmitButtonId}
        onSubmit={handleSubmitAddons}
      >
        <SelectProductsStep
          closeModal={closeModal}
          creditCount={creditCount}
          setHasEnoughCredits={setHasEnoughCredits}
        />
      </WizardStep>

      <WizardStep
        enabled={isBusinessPlan}
        onSubmit={handleSubmitAddons}
        backButtonId="syndication"
        submitButtonId={addonSubmitButtonId}
      >
        <AddonStep setHasAddons={setHasAddons} />
      </WizardStep>

      <WizardStep
        validationSchema={checkoutValidationSchema}
        enabled={showCheckout}
        backButtonId="syndication"
        submitButtonId={`usage-credit-${planText}-checkout-payment-dialog-multiplatform-adv-submit-click`}
      >
        <CheckoutStep />
      </WizardStep>
    </Wizard>
  )
}

export default ModalContent
